import '#lit/components/base/icon/index.js';
import '../canvas_window/index.js';

import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
  chainCmd,
} from '#lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import {Point, isPointTuple} from '#lit/lib/point.js';
import {html} from 'lit';
import type {StyleInfo} from 'lit/directives/style-map.js';
import {styleMap} from 'lit/directives/style-map.js';
import type {CursorEvent} from '#lit/lib/cursor.js';
import {sortCanvasElements, calculateScale} from './utils.js';
import {change} from '#lit/utils/commands/index.js';
import type {Nullable} from '#lit/types/general.js';
import type FlyCanvas from './index.js';
import CanvasElement from '../canvas_element/index.js';
import type CanvasConnection from '../canvas_connection/index.js';
import CanvasNode from '../canvas_node/index.js';
import CanvasGroup from '../canvas_group/index.js';
import {isCanvasGroupType, isCanvasNodeType} from '#lit/types/canvas.js';
import {getNodeFromPath} from '../canvas_node/utils.js';

const INITIAL_SCALE_FACTOR = 1;
const INITIAL_CONTEXT = 'moving';
const SCALE_RESET_KEY = '0';
const SCALE_INCREMENT_FROM_WHEEL_EVENT = 0.07;

export type CanvasCursorContext = 'moving' | 'dragging' | 'scaling' | 'idle';

export type Model = {
  position: Point;
  scale: number;
  planeRect: Nullable<DOMRect>;
  context: CanvasCursorContext;
  edgeRegistry: Map<string, CanvasConnection>;
  nodeRegistry: Map<string, CanvasNode>;
  groupRegistry: Map<string, CanvasGroup>;
  selectedNodes: Set<string>;
};

export const Msg = makeVariants({
  changePosition: Variant<Point>()('change-position'),
  setScale: Variant<number>()('set-scale'),
  setContext: Variant<CanvasCursorContext>()('set-context'),
  setPlaneRect: Variant<Nullable<DOMRect>>()('set-plane-rect'),
  deselectAll: Variant<void>()('deselect-all'),
  removeSelectedNode: Variant<string>()('remove-selected-node'),
  addSelectedNode: Variant<string>()('add-selected-node'),
  registerNode: Variant<CanvasNode>()('register-node'),
  registerGroup: Variant<CanvasGroup>()('register-group'),
  removeNode: Variant<CanvasNode['key']>()('remove-node'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function mapCursorEvents(host: FlyCanvas) {
  // TODO(joshua):
  // Make search for nodes a function of the cursor event itself somehow....
  const targetIsPlane = (host: FlyCanvas, event: CursorEvent): boolean => {
    return !!(
      host.$plane &&
      host.$background &&
      event.cursor.diff &&
      event.dragTarget instanceof Element &&
      [host, host.$plane, host.$background].some(el => {
        return !!(
          event.dragTarget?.isSameNode(el) ||
          host.$background?.contains(event.dragTarget!) ||
          (!CanvasNode.isInPath(event.dragPath ?? []) &&
            !CanvasGroup.isInPath(event.dragPath ?? []))
        );
      })
    );
  };

  host.cursor
    .on('drop', () => {
      host.handleUpdate(Msg.setContext('moving'));
    })
    .on('drag', event => {
      const node = getNodeFromPath(event.dragPath ?? []);
      const group = CanvasGroup.getGroupFromPath(event.dragPath ?? []);

      if (node) {
        node.move(event.cursor.diff!.divide(Point.from(host.model.scale)));
      }
      if (group) {
        group.move(event.cursor.diff!.divide(Point.from(host.model.scale)));
      }
    })
    .on('drag', event => {
      if (!targetIsPlane(host, event) || host.$window?.model.open) return;
      host.handleUpdate(Msg.changePosition(event.cursor.diff!));
      host.handleUpdate(Msg.setContext('dragging'));
    })
    .on('wheel', event => {
      const nativeEvent = event.native as WheelEvent;
      if (!nativeEvent.ctrlKey) return;

      nativeEvent.preventDefault();

      host.handleUpdate(Msg.setContext('scaling'));

      const multiplier = nativeEvent.deltaY > 0 ? 1 : -1;
      const updatedScaledValue = calculateScale(
        host.model.scale,
        multiplier * SCALE_INCREMENT_FROM_WHEEL_EVENT
      );

      host.handleUpdate(Msg.setScale(updatedScaledValue));
    });

  addEventListener('keyup', event => {
    if (event.ctrlKey && event.key === SCALE_RESET_KEY) {
      host.handleUpdate(Msg.setScale(INITIAL_SCALE_FACTOR));
    }
  });
}

const resetContext = (() => {
  let timeoutId: Nullable<ReturnType<typeof setTimeout>> = null;
  return function (host: FlyCanvas) {
    !!timeoutId && clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      if (host.model.context === 'scaling') {
        host.handleUpdate(Msg.setContext('idle'));
      }
    }, 200);
  };
})();

function setPlaneRect(host: FlyCanvas) {
  const planeRect = host.$plane?.getBoundingClientRect() ?? null;
  if (planeRect) {
    host.handleUpdate(Msg.setPlaneRect(planeRect));
  }
}

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const initialPosition = props.get('initialPosition');

  return [
    {
      context: INITIAL_CONTEXT,
      scale: INITIAL_SCALE_FACTOR,
      edgeRegistry: new Map(),
      nodeRegistry: new Map(),
      groupRegistry: new Map(),
      selectedNodes: new Set(),
      position: isPointTuple(initialPosition)
        ? Point.fromTuple(initialPosition)
        : Point.from(0, 0),
      planeRect: null,
    } as const,
    cmd(chainCmd(mapCursorEvents, setPlaneRect)),
  ];
}

// -- Update
function updateCursor(host: FlyCanvas, context: CanvasCursorContext) {
  Object.assign(host.style, {
    cursor: context === 'dragging' ? 'grabbing' : 'default',
  });
}

function update(
  msg: Msg,
  model: Readonly<Model>,
  cmd: Cmd
): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-context':
      return [
        {...model, context: msg.value},
        cmd((host: FlyCanvas) => {
          updateCursor(host, msg.value);
          setPlaneRect(host);
          resetContext(host);
        }),
      ] as const;

    case 'set-scale':
      return [{...model, scale: msg.value}, cmd(setPlaneRect)] as const;

    case 'register-node':
      return [
        {
          ...model,
          nodeRegistry: new Map([
            ...model.nodeRegistry.entries(),
            [msg.value.key, msg.value],
          ]),
        },
        cmd(change),
      ] as const;

    case 'register-group':
      return [
        {
          ...model,
          groupRegistry: new Map([
            ...model.groupRegistry.entries(),
            [msg.value.key, msg.value],
          ]),
        },
        cmd(change),
      ] as const;

    case 'remove-node': {
      model.nodeRegistry.delete(msg.value);

      return [
        {
          ...model,
          nodeRegistry: new Map(model.nodeRegistry),
        },
        cmd(change),
      ] as const;
    }

    case 'deselect-all':
      return [
        model,
        cmd((host: FlyCanvas) => {
          host.model.selectedNodes.forEach(key => {
            const target =
              host.model.nodeRegistry.get(key) ??
              host.model.groupRegistry.get(key);
            if (target) target.deselect();
          });
        }),
      ];

    case 'add-selected-node': {
      return {
        ...model,
        selectedNodes: new Set([...model.selectedNodes.values(), msg.value]),
      } as const;
    }

    case 'remove-selected-node': {
      const values = Array.from(model.selectedNodes.values()).filter(k => {
        return k !== msg.value;
      });

      return {...model, selectedNodes: new Set(values)} as const;
    }

    case 'set-plane-rect': {
      return {
        ...model,
        planeRect: msg.value,
      } as const;
    }

    case 'change-position': {
      const updatedPosition = model.position.add(msg.value);
      return [
        {
          ...model,
          position: updatedPosition,
        },
        cmd(chainCmd(change, setPlaneRect)),
      ] as const;
    }
  }
}

// -- View

function onSlotChange(host: FlyCanvas, event: Event) {
  const childNodes = sortCanvasElements(
    (event.target as HTMLSlotElement).assignedNodes({
      flatten: true,
    })
  );

  for (const node of childNodes) {
    if (!(node instanceof CanvasElement) || node.model.attached) continue;

    node.attach(host);

    if (isCanvasNodeType(node)) host.registerNode(node);
    if (isCanvasGroupType(node)) host.registerGroup(node);
  }

  setPlaneRect(host);
}

function planeStyles(model: Readonly<Model>): StyleInfo {
  const {x, y} = model.position;
  return {
    transition: model.context === 'scaling' ? '0.1s linear' : '',
    transform: `translateX(${x}px) translateY(${y}px) scale(${model.scale})`,
  };
}

function view(this: FlyCanvas, model: Readonly<Model>) {
  const {position} = model;
  return html`
    <!-- Dot background. -->
    <svg id="canvas-background">
      <pattern
        id="dots"
        x="${position.x}"
        y="${position.y}"
        width="20"
        height="20"
        patternUnits="userSpaceOnUse"
        patternTransform="translate(-0.5,-0.5)"
      >
        <circle
          cx="0.5"
          cy="0.5"
          r="0.5"
          fill="var(--fly-color-body-alt)"
        ></circle>
      </pattern>
      <rect x="0" y="0" width="100%" height="100%" fill="url(#dots)"></rect>
    </svg>
    <!-- Canvas plane -->
    <div id="canvas-plane" style=${styleMap(planeStyles(model))}>
      <slot @slotchange=${(e: Event) => onSlotChange(this, e)}></slot>
    </div>
    <!-- <div id="coordinates">${position.x}, ${position.y}</div> -->

    <!-- Action buttons -->
    <!-- TODO(joshua): Actually make a control pannel here. -->

    <!-- Canvas windows -->
    <canvas-window id="canvas-window"></canvas-window>
  `;
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
